import { useTranslation } from "react-i18next";
import { SafeText } from "@/shared/components/SafeText";
import type { AssignmentResultRow } from "./access-client";
import { isNoChangeOutcome } from "./assignment-results";
import { idsWithSharedName, shortId } from "./location-label";

interface AssignmentResultListProps {
  readonly results: readonly AssignmentResultRow[];
  /** Location id -> name, for the ones the admin saw or ticked. */
  readonly names: ReadonlyMap<string, string>;
  readonly testId: string;
}

/**
 * One line per requested location with exactly what the API did with it, in
 * the order it answered: nothing is summarised away, so a no-op is as visible
 * as a change. A live region, so a screen reader announces the result when it
 * replaces the form. Same-named locations get a short id to tell them apart.
 */
export function AssignmentResultList({ results, names, testId }: AssignmentResultListProps) {
  const { t } = useTranslation();
  const needsSuffix = idsWithSharedName(
    results.map((row) => ({ id: row.entityId, name: names.get(row.entityId) ?? "" })),
  );
  return (
    <div role="status">
      <ul
        data-testid={testId}
        className="divide-y divide-border rounded-md border border-border text-sm"
      >
        {results.map((row) => (
          <li
            key={`${row.action}:${row.entityId}`}
            className="flex min-w-0 items-center justify-between gap-3 px-3 py-2"
          >
            <span className="min-w-0 flex-1 truncate">
              <SafeText fallback={t("organizationAccess.manageLocations.unnamed")}>
                {names.get(row.entityId) ?? ""}
              </SafeText>
              {needsSuffix.has(row.entityId) && (
                <span className="ml-2 font-mono text-xs text-muted-foreground">
                  {shortId(row.entityId)}
                </span>
              )}
            </span>
            <span
              className={
                isNoChangeOutcome(row.outcome)
                  ? "shrink-0 text-xs text-muted-foreground"
                  : "shrink-0 text-xs font-medium text-foreground"
              }
            >
              {t(`organizationAccess.manageLocations.outcome.${row.outcome}`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
